/**
 * App shell — the boot sequence that makes the SPA whole.
 *
 *   loadRuntimeConfig()  →  AuthProvider  →  token? shell : <Login/>
 *
 * Boot:
 *   1. Fetch /config.json (loadRuntimeConfig, throws a typed ConfigError). The
 *      local-dev fallback (localhost dev config + visible DEV banner) fires ONLY
 *      when the config is genuinely absent — a 404, or the vite-dev-server SPA
 *      fallback that returns index.html (a 'parse' failure with text/html). Every
 *      other failure (403/500, a corrupt application/json body, a network error)
 *      is a real deploy malfunction and renders a loud error screen instead of
 *      silently pointing the console at localhost. In a real deploy the
 *      DashboardStack BucketDeployment writes config.json, so the dev fallback
 *      never fires in production.
 *   2. Wrap the tree in QueryProvider (QueryClientProvider + ApiProvider) and
 *      AuthProvider. ApiProvider lives INSIDE AuthProvider (it reads useAuth);
 *      QueryProvider composes them in that order (see queries.ts QueryProvider).
 *   3. token absent → <Login/>; present → the cockpit shell (header + hash-routed
 *      tab bar + active tab).
 *
 * Routing is hash-based (#settings / #universe / #runs / #health), default
 * #settings — no router dependency, just window.location.hash + a hashchange
 * listener. This keeps the static-site deploy trivial (no server rewrites).
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  AuthProvider,
  ConfigError,
  loadRuntimeConfig,
  useAuth,
  type RuntimeConfig,
} from './lib/auth';
import { QueryProvider, makeQueryClient } from './lib/queries';
import Login from './components/Login';
import { InviteFailureBanner } from './components/InviteFailureBanner';
import SettingsTab from './tabs/SettingsTab';
import UniverseTab from './tabs/UniverseTab';
import RunsTab from './tabs/RunsTab';
import HealthTab from './tabs/HealthTab';
import UsageTab from './tabs/UsageTab';
import HomeTab from './tabs/HomeTab';
import TeamTab from './tabs/TeamTab';
import AdminTab from './tabs/AdminTab';

// ---------------------------------------------------------------------------
// Local-dev fallback config. Only ever used when /config.json is absent (a real
// deploy always ships it via the DashboardStack BucketDeployment).
// ---------------------------------------------------------------------------

const DEV_CONFIG: RuntimeConfig = {
  apiUrl: 'http://localhost:3001',
  guardiusUrl: 'https://guardius.penseur.ai',
  env: 'paper',
};

// ---------------------------------------------------------------------------
// Tabs.
// ---------------------------------------------------------------------------

// `ownerOnly` / `adminOnly` tabs are hidden from the nav AND non-routable for
// non-owners / non-admins: navigating to a gated hash resolves to Home (see
// resolveActiveTab / isTabVisible).
const TABS = [
  { id: 'home', label: 'Home', Component: HomeTab },
  { id: 'settings', label: 'Settings', Component: SettingsTab },
  { id: 'universe', label: 'Universe', Component: UniverseTab },
  { id: 'runs', label: 'Runs', Component: RunsTab },
  { id: 'health', label: 'Health', Component: HealthTab },
  { id: 'usage', label: 'Usage', Component: UsageTab },
  { id: 'team', label: 'Team', Component: TeamTab, ownerOnly: true },
  { id: 'admin', label: 'Admin', Component: AdminTab, adminOnly: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_IDS = TABS.map((t) => t.id) as readonly string[];

/** Read the active tab from the URL hash, defaulting to `home` for an empty
 *  or unrecognized hash. Owner-only gating is applied separately by the Shell
 *  (resolveActiveTab) so a non-owner on `#team` falls back to Home. */
function tabFromHash(): TabId {
  const raw = window.location.hash.replace(/^#/, '');
  return (TAB_IDS.includes(raw) ? raw : 'home') as TabId;
}

/** Subscribe to hashchange and report the current tab. */
function useHashTab(): TabId {
  const [tab, setTab] = useState<TabId>(tabFromHash);
  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  return tab;
}

// ---------------------------------------------------------------------------
// Shell (rendered only when authenticated — reads useAuth).
// ---------------------------------------------------------------------------

/** Whether a tab is visible/routable for the current viewer. Owner-only tabs are
 *  hidden from non-owners; admin-only tabs from non-platform-admins. */
function isTabVisible(
  tab: (typeof TABS)[number],
  isOwner: boolean,
  isPlatformAdmin: boolean,
): boolean {
  if ('ownerOnly' in tab && tab.ownerOnly && !isOwner) return false;
  if ('adminOnly' in tab && tab.adminOnly && !isPlatformAdmin) return false;
  return true;
}

function Shell() {
  const { config, orgId, address, isOwner, isPlatformAdmin, signOut } = useAuth();
  const tab = useHashTab();
  // Tabs the viewer may see/route to. An owner-only / admin-only tab is dropped
  // for a non-owner / non-admin, so navigating to a gated hash falls through to
  // the default (Home).
  const visibleTabs = TABS.filter((t) => isTabVisible(t, isOwner, isPlatformAdmin));
  const active = visibleTabs.find((t) => t.id === tab) ?? visibleTabs[0];
  const ActiveTab = active.Component;

  return (
    <div className="app-shell">
      <header className="app-header">
        <span className="app-title">Penseur Trading Console</span>
        <span className="env-pill" title={`org ${orgId ?? '—'}`}>
          {orgId ?? '—'} · {config.env}
        </span>
        <a
          className="app-version"
          href={`https://github.com/penseurai/penseur-trading/commit/${__BUILD_SHA__}`}
          target="_blank"
          rel="noreferrer"
          title="deployed commit"
        >
          {__BUILD_SHA__}
        </a>
        <span className="app-header-spacer" />
        {address && (
          <span className="app-wallet" title={address}>
            {address}
          </span>
        )}
        <button type="button" className="btn app-signout" onClick={signOut}>
          Sign out
        </button>
      </header>

      <nav className="tab-bar" aria-label="Console sections">
        {visibleTabs.map((t) => (
          <a
            key={t.id}
            href={`#${t.id}`}
            className="tab"
            aria-current={t.id === active.id ? 'page' : undefined}
          >
            {t.label}
          </a>
        ))}
      </nav>

      <InviteFailureBanner />

      <main className="tab-content">
        <ActiveTab />
      </main>
    </div>
  );
}

/** Auth gate: token absent → Login, present → Shell. */
function Gate() {
  const { token } = useAuth();
  return token ? <Shell /> : <Login />;
}

// ---------------------------------------------------------------------------
// Boot.
// ---------------------------------------------------------------------------

type BootState =
  | { phase: 'loading' }
  | { phase: 'ready'; config: RuntimeConfig; isDev: boolean }
  | { phase: 'error'; message: string };

/** Decide whether a config-load failure is the "config genuinely absent → local
 *  dev" case (→ dev fallback) or a real malfunction (→ loud error screen).
 *
 *  Dev-fallback ONLY for:
 *    - kind 'not-found' (HTTP 404 — no config.json on the origin), or
 *    - kind 'parse' whose response was text/html — the vite dev server's SPA
 *      history fallback serving index.html for a missing /config.json (verified:
 *      vite returns 200 + Content-Type: text/html + an <!doctype html> body, so
 *      res.json() throws). This is the ONLY parse case that's really "absent".
 *
 *  Everything else stays loud: a 403/500 ('http'), a network rejection
 *  ('network'), and crucially a 'parse' on an application/json body — a corrupt
 *  or truncated config.json from a real deploy must NOT masquerade as dev mode. */
function isGenuinelyAbsent(err: unknown): boolean {
  if (!(err instanceof ConfigError)) return false;
  if (err.kind === 'not-found') return true;
  if (err.kind === 'parse') {
    return (err.contentType ?? '').toLowerCase().includes('text/html');
  }
  return false;
}

export default function App() {
  const [boot, setBoot] = useState<BootState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const config = await loadRuntimeConfig();
        if (!cancelled) setBoot({ phase: 'ready', config, isDev: false });
      } catch (err) {
        if (cancelled) return;
        if (isGenuinelyAbsent(err)) {
          // config.json genuinely absent (404 or vite SPA fallback) → local-dev
          // fallback + DEV banner.
          setBoot({ phase: 'ready', config: DEV_CONFIG, isDev: true });
        } else {
          // A real malfunction (403/500, corrupt JSON, network) — fail loudly
          // rather than silently pointing the console at localhost.
          const message = err instanceof Error ? err.message : String(err);
          setBoot({ phase: 'error', message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (boot.phase === 'loading') {
    return <div className="boot-fallback">Loading config…</div>;
  }

  if (boot.phase === 'error') {
    return (
      <div className="boot-error" role="alert">
        <strong>Configuration failed to load</strong>
        <p>{boot.message}</p>
      </div>
    );
  }

  return (
    <Providers config={boot.config}>
      {boot.isDev && (
        <div className="dev-banner" role="status">
          DEV — no config.json found; using localhost fallback ({DEV_CONFIG.apiUrl})
        </div>
      )}
      <Gate />
    </Providers>
  );
}

/** Compose the auth + query providers in the required order: AuthProvider on the
 *  outside (owns config + token), QueryProvider inside it (ApiProvider reads
 *  useAuth). The QueryClient is created once and held for the app's lifetime. */
function Providers({ config, children }: { config: RuntimeConfig; children: ReactNode }) {
  const [client] = useState(makeQueryClient);
  return (
    <AuthProvider config={config}>
      <QueryProvider client={client}>{children}</QueryProvider>
    </AuthProvider>
  );
}
